import { useState, useEffect } from 'react';

// Add global helper for development
if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
  (window as any).refreshCurrency = () => {
    localStorage.removeItem('userCountry');
    localStorage.removeItem('userCurrencySymbol');
    window.location.reload();
  };
  console.log('💰 Currency Debug: Use refreshCurrency() to force re-detection');
}

// Simple currency symbols mapping
const CURRENCY_SYMBOLS = {
  USD_US: '$',      // For users in the US
  USD_INTL: 'US$',  // For users outside the US (to avoid confusion with local dollars)
  CHF: 'CHF ',
  EUR: '€',
  GBP: '£',
};

// Country to currency mapping
const COUNTRY_CURRENCY_MAP: Record<string, keyof typeof CURRENCY_SYMBOLS> = {
  // United States - show just $ for US users
  US: 'USD_US',
  
  // Switzerland
  CH: 'CHF',
  
  // Eurozone countries (all 20 countries that use the Euro)
  AD: 'EUR', // Andorra
  AT: 'EUR', // Austria
  BE: 'EUR', // Belgium
  CY: 'EUR', // Cyprus
  DE: 'EUR', // Germany
  EE: 'EUR', // Estonia
  ES: 'EUR', // Spain
  FI: 'EUR', // Finland
  FR: 'EUR', // France
  GR: 'EUR', // Greece
  HR: 'EUR', // Croatia (joined 2023)
  IE: 'EUR', // Ireland
  IT: 'EUR', // Italy
  LT: 'EUR', // Lithuania
  LU: 'EUR', // Luxembourg
  LV: 'EUR', // Latvia
  MC: 'EUR', // Monaco
  ME: 'EUR', // Montenegro (uses Euro unofficially)
  MT: 'EUR', // Malta
  NL: 'EUR', // Netherlands
  PT: 'EUR', // Portugal
  SI: 'EUR', // Slovenia
  SK: 'EUR', // Slovakia
  SM: 'EUR', // San Marino
  VA: 'EUR', // Vatican City
  XK: 'EUR', // Kosovo (uses Euro unofficially)
  
  // United Kingdom
  GB: 'GBP',
  
  // European countries that are NOT in Eurozone (use their own currencies or US$)
  // Sweden, Denmark, Norway, Poland, Czech Republic, Hungary, etc. will default to US$
  
  // All other countries default to international USD (US$)
  // This includes Australia, Canada, New Zealand, Sweden, Denmark, Norway, etc.
};

/**
 * Get user's country from timezone (fallback method)
 */
function getUserCountryFromTimezone(): string | null {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const timezoneCountryMap: Record<string, string> = {
      // US timezones
      'America/New_York': 'US',
      'America/Chicago': 'US', 
      'America/Denver': 'US',
      'America/Los_Angeles': 'US',
      'America/Phoenix': 'US',
      'America/Anchorage': 'US',
      'America/Honolulu': 'US',
      
      // Switzerland
      'Europe/Zurich': 'CH',
      
      // UK
      'Europe/London': 'GB',
      
      // Eurozone countries
      'Europe/Berlin': 'DE',        // Germany
      'Europe/Paris': 'FR',         // France
      'Europe/Rome': 'IT',          // Italy
      'Europe/Madrid': 'ES',        // Spain
      'Europe/Amsterdam': 'NL',     // Netherlands
      'Europe/Brussels': 'BE',      // Belgium
      'Europe/Vienna': 'AT',        // Austria
      'Europe/Dublin': 'IE',        // Ireland
      'Europe/Lisbon': 'PT',        // Portugal
      'Europe/Helsinki': 'FI',      // Finland
      'Europe/Athens': 'GR',        // Greece
      'Europe/Luxembourg': 'LU',    // Luxembourg
      'Europe/Ljubljana': 'SI',     // Slovenia
      'Europe/Bratislava': 'SK',    // Slovakia
      'Europe/Tallinn': 'EE',       // Estonia
      'Europe/Riga': 'LV',          // Latvia
      'Europe/Vilnius': 'LT',       // Lithuania
      'Europe/Valletta': 'MT',      // Malta
      'Europe/Nicosia': 'CY',       // Cyprus
      'Europe/Zagreb': 'HR',        // Croatia
      
      // Non-Eurozone European countries (will show US$ by default)
      'Europe/Stockholm': 'SE',     // Sweden (uses SEK, but we'll show US$ for simplicity)
      'Europe/Copenhagen': 'DK',    // Denmark (uses DKK, but we'll show US$ for simplicity)
      'Europe/Oslo': 'NO',          // Norway (uses NOK, but we'll show US$ for simplicity)
      'Europe/Warsaw': 'PL',        // Poland (uses PLN, but we'll show US$ for simplicity)
      'Europe/Prague': 'CZ',        // Czech Republic (uses CZK, but we'll show US$ for simplicity)
      'Europe/Budapest': 'HU',      // Hungary (uses HUF, but we'll show US$ for simplicity)
      'Europe/Bucharest': 'RO',     // Romania (uses RON, but we'll show US$ for simplicity)
      'Europe/Sofia': 'BG',         // Bulgaria (uses BGN, but we'll show US$ for simplicity)
    };
    
    return timezoneCountryMap[timezone] || null;
  } catch (error) {
    console.warn('Timezone detection failed:', error);
    return null;
  }
}

/**
 * Get user's country from IP geolocation (primary method for VPN detection)
 */
async function getUserCountryFromIP(): Promise<string> {
  // Try CORS-friendly services (ordered by reliability)
  const services = [
    {
      name: 'ipapi.co',
      url: 'https://ipapi.co/json/',
      process: async (response: Response) => {
        const data = await response.json();
        return data.country_code || 'XX';
      }
    },
    {
      name: 'ip-api.com',
      url: 'https://ip-api.com/json/?fields=countryCode',
      process: async (response: Response) => {
        const data = await response.json();
        return data.countryCode || 'XX';
      }
    }
  ];

  for (const service of services) {
    try {
      console.log(`🌍 Currency: Trying ${service.name}...`);
      
      const timeoutId = setTimeout(() => {
        throw new Error(`${service.name} timeout`);
      }, 5000);
      
      const response = await fetch(service.url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const countryCode = await service.process(response);
        const cleanCountryCode = countryCode.trim().toUpperCase();
        
        // Validate country code format (should be 2 letters)
        if (cleanCountryCode.length === 2 && /^[A-Z]{2}$/.test(cleanCountryCode)) {
          console.log(`🌍 Currency: ${service.name} detected:`, cleanCountryCode);
          return cleanCountryCode;
        }
      }
    } catch (error) {
      console.warn(`🌍 Currency: ${service.name} failed:`, error);
      continue; // Try next service
    }
  }
  
  console.warn('🌍 Currency: All IP geolocation services failed');
  return 'XX';
}

interface UseCurrencySymbolReturn {
  currencySymbol: string;
  currencyCode: keyof typeof CURRENCY_SYMBOLS;
  countryCode: string;
  isLoading: boolean;
  forceRefresh: () => void;
}

/**
 * Custom hook for getting the appropriate currency symbol based on user location
 * Prioritizes IP geolocation (for VPN detection) over timezone
 */
export function useCurrencySymbol(): UseCurrencySymbolReturn {
  // Initialize with cached values if available, otherwise use loading defaults
  const getCachedOrDefault = () => {
    const storedCountry = localStorage.getItem('userCountry');
    const storedSymbol = localStorage.getItem('userCurrencySymbol');
    
    if (storedCountry && storedSymbol) {
      return {
        country: storedCountry,
        symbol: storedSymbol,
        code: COUNTRY_CURRENCY_MAP[storedCountry] || 'USD_INTL',
        loading: false
      };
    }
    
    // If no cache, try timezone as immediate fallback to avoid US$ flash
    const timezoneCountry = getUserCountryFromTimezone();
    if (timezoneCountry) {
      const timezoneCode = COUNTRY_CURRENCY_MAP[timezoneCountry] || 'USD_INTL';
      const timezoneSymbol = CURRENCY_SYMBOLS[timezoneCode];
      return {
        country: timezoneCountry,
        symbol: timezoneSymbol,
        code: timezoneCode,
        loading: true // Still loading IP detection
      };
    }
    
    // Last resort: default values
    return {
      country: 'XX',
      symbol: 'US$',
      code: 'USD_INTL',
      loading: true
    };
  };

  const initial = getCachedOrDefault();
  const [currencySymbol, setCurrencySymbol] = useState<string>(initial.symbol);
  const [currencyCode, setCurrencyCode] = useState<keyof typeof CURRENCY_SYMBOLS>(initial.code);
  const [countryCode, setCountryCode] = useState<string>(initial.country);
  const [isLoading, setIsLoading] = useState<boolean>(initial.loading);

  const detectCurrencySymbol = async () => {
    try {
      // Don't show loading if we have good cached data
      const storedCountry = localStorage.getItem('userCountry');
      if (!storedCountry) {
        setIsLoading(true);
      }
      
      let detectedCountry = 'XX';
      
      // PRIORITY 1: Try IP geolocation first (detects VPN location)
      try {
        detectedCountry = await getUserCountryFromIP();
        console.log('🌍 Currency: IP geolocation detected:', detectedCountry);
        
        // If IP detection returns unknown, try timezone as fallback
        if (detectedCountry === 'XX') {
          throw new Error('IP returned unknown country');
        }
      } catch (ipError) {
        console.warn('🌍 Currency: IP geolocation failed, trying timezone fallback...', ipError);
        
        // PRIORITY 2: Fallback to timezone detection
        const timezoneCountry = getUserCountryFromTimezone();
        if (timezoneCountry) {
          detectedCountry = timezoneCountry;
          console.log('🕐 Currency: Timezone fallback detected:', detectedCountry);
        }
      }
      
      const detectedCurrencyCode = COUNTRY_CURRENCY_MAP[detectedCountry] || 'USD_INTL';
      const detectedSymbol = CURRENCY_SYMBOLS[detectedCurrencyCode];
      
      console.log('💰 Currency: Final detection -', { 
        country: detectedCountry, 
        symbol: detectedSymbol,
        method: detectedCountry !== 'XX' ? 'IP or Timezone' : 'Default'
      });
      
      // Only update if the detection result is different from current
      if (detectedCountry !== countryCode || detectedSymbol !== currencySymbol) {
        setCountryCode(detectedCountry);
        setCurrencyCode(detectedCurrencyCode);
        setCurrencySymbol(detectedSymbol);
        
        // Store in localStorage for future use
        localStorage.setItem('userCountry', detectedCountry);
        localStorage.setItem('userCurrencySymbol', detectedSymbol);
      }
      
    } catch (error) {
      console.warn('Currency detection failed:', error);
      
      // Use stored values as fallback
      const storedCountry = localStorage.getItem('userCountry') || 'XX';
      const storedSymbol = localStorage.getItem('userCurrencySymbol') || 'US$';
      
      setCountryCode(storedCountry);
      setCurrencyCode(COUNTRY_CURRENCY_MAP[storedCountry] || 'USD_INTL');
      setCurrencySymbol(storedSymbol);
    } finally {
      setIsLoading(false);
    }
  };

  const forceRefresh = () => {
    localStorage.removeItem('userCountry');
    localStorage.removeItem('userCurrencySymbol');
    detectCurrencySymbol();
  };

  useEffect(() => {
    // Always run IP detection, but don't show loading if we have cached data
    detectCurrencySymbol();
  }, []);

  return {
    currencySymbol,
    currencyCode,
    countryCode,
    isLoading,
    forceRefresh,
  };
}
